/**
 * Route shape of the pay page `/job/{id}/pay` and the archived ad `/job/{id}/pay/{archiveId}`.
 * The job detail route is `/job/{id}/{companyId}/{slug?}`, so "pay" would otherwise be read as a
 * company id: both routes use this to agree on which paths are the pay pages.
 */
import { LOCALIZED_PATHS } from '../constants/url-constants';

const JOB_SEGMENTS = [LOCALIZED_PATHS.en.job, LOCALIZED_PATHS.fi.job, LOCALIZED_PATHS.sv.job];
const PAY_PATH = new RegExp(`^/(?:${JOB_SEGMENTS.join('|')})/([^/]+)/pay(?:/([^/]+))?/?$`);

export interface PayRouteParams {
  jobId: string;
  archiveId?: string;
}

function decode(segment: string): string | null {
  try {
    return decodeURIComponent(segment);
  } catch {
    return null;
  }
}

/** The pay page params for a (delocalized, locale-less) pathname, or null when it is not a pay page. */
export function parsePayRoutePath(pathname: string | null | undefined): PayRouteParams | null {
  if (!pathname) return null;
  const match = PAY_PATH.exec(pathname.split('?')[0]);
  if (!match) return null;
  const jobId = decode(match[1]);
  if (!jobId || jobId.length < 3 || /[/\\]/.test(jobId)) return null;
  if (match[2] === undefined) return { jobId };
  const archiveId = decode(match[2]);
  if (!archiveId || /[/\\]/.test(archiveId)) return null;
  return { jobId, archiveId };
}

export function isPayRoutePath(pathname: string | null | undefined): boolean {
  return parsePayRoutePath(pathname) !== null;
}

type PayLocale = 'en' | 'fi' | 'sv';

function toPayLocale(locale: string): PayLocale {
  return locale === 'fi' || locale === 'sv' ? locale : 'en';
}

/** Locale-prefixed href of the pay page (`/fi/tyopaikka/{id}/pay`) or of one archived ad. */
export function payPageHref(locale: string, jobId: string, archiveId?: string): string {
  const l = toPayLocale(locale);
  const base = `/${l}/${LOCALIZED_PATHS[l].job}/${encodeURIComponent(jobId)}/pay`;
  return archiveId ? `${base}/${encodeURIComponent(archiveId)}` : base;
}

/** Locale-prefixed href of the job page itself (the company segment is optional in the route). */
export function jobPageHref(locale: string, jobId: string, encodedCompanySegment?: string): string {
  const l = toPayLocale(locale);
  const base = `/${l}/${LOCALIZED_PATHS[l].job}/${encodeURIComponent(jobId)}`;
  return encodedCompanySegment ? `${base}/${encodedCompanySegment}` : base;
}
